import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getCompany, getCompanyBankAccounts, getSignatories, getYtdEntries } from 'services/company';
import { getPaySchedules } from 'services/paySchedules';
import { getWorkplaces } from 'services/workplaces';
import { getEmployees } from 'services/employees';
import { getDepartments } from 'services/departments';

function useSetupChecks() {
    const { data: company } = useQuery({ queryKey: ['company'], queryFn: getCompany, staleTime: 60000 });
    const { data: bankAccountsRaw } = useQuery({ queryKey: ['company-bank-accounts'], queryFn: getCompanyBankAccounts, staleTime: 60000 });
    const { data: schedulesRaw } = useQuery({ queryKey: ['pay-schedules'], queryFn: getPaySchedules, staleTime: 60000 });
    const { data: workplacesRaw } = useQuery({ queryKey: ['workplaces'], queryFn: getWorkplaces, staleTime: 60000 });
    const { data: employeesRaw } = useQuery({ queryKey: ['employees'], queryFn: getEmployees, staleTime: 60000 });
    const { data: departmentsRaw } = useQuery({ queryKey: ['departments'], queryFn: getDepartments, staleTime: 60000 });
    const { data: signatoriesRaw } = useQuery({ queryKey: ['signatories'], queryFn: getSignatories, staleTime: 60000 });
    const { data: ytdRaw }         = useQuery({ queryKey: ['ytd-entries'], queryFn: getYtdEntries, staleTime: 60000 });

    const bankAccounts = Array.isArray(bankAccountsRaw) ? bankAccountsRaw : (bankAccountsRaw?.data ?? []);
    const schedules    = Array.isArray(schedulesRaw)    ? schedulesRaw    : (schedulesRaw?.data    ?? []);
    const workplaces   = Array.isArray(workplacesRaw)   ? workplacesRaw   : (workplacesRaw?.data   ?? []);
    const employees    = Array.isArray(employeesRaw)    ? employeesRaw    : (employeesRaw?.data    ?? []);
    const departments  = Array.isArray(departmentsRaw)  ? departmentsRaw  : (departmentsRaw?.data  ?? []);
    const signatories  = Array.isArray(signatoriesRaw)  ? signatoriesRaw  : (signatoriesRaw?.data  ?? []);
    const ytdEntries   = Array.isArray(ytdRaw)           ? ytdRaw           : (ytdRaw?.data          ?? []);

    const checks = [
        {
            id: 'company-info',
            label: 'Company Information',
            description: 'Legal name, EIN, and address',
            done: !!(company?.ein && (company?.addressLine1 || company?.address)),
            hash: 'company-information',
            required: true,
        },
        {
            id: 'bank-account',
            label: 'Withdrawal Bank Account',
            description: 'At least one bank account for payroll ACH',
            done: bankAccounts.length > 0,
            hash: 'withdrawal-bank-account',
            required: true,
        },
        {
            id: 'federal-taxes',
            label: 'Federal & State Taxes',
            description: 'SUTA rate and federal deposit schedule',
            done: !!(company?.ein && company?.federalDepositSchedule),
            hash: 'federal-state-taxes',
            required: true,
        },
        {
            id: 'pay-schedules',
            label: 'Pay Schedules',
            description: 'At least one pay schedule (weekly, bi-weekly, etc.)',
            done: schedules.length > 0,
            hash: 'pay-schedules',
            required: true,
        },
        {
            id: 'workplaces',
            label: 'Workplaces',
            description: 'At least one workplace location',
            done: workplaces.length > 0,
            hash: 'workplaces',
            required: true,
        },
        {
            id: 'departments',
            label: 'Departments',
            description: 'At least one department for employee grouping',
            done: departments.length > 0,
            hash: 'departments',
            required: false,
        },
        {
            id: 'employees',
            label: 'Employees Added',
            description: 'At least one active employee',
            done: employees.filter(e => e.active !== false && e.isActive !== false).length > 0,
            hash: null,
            link: '/employees',
            required: true,
        },
        {
            id: 'employee-setup',
            label: 'Employee Setup',
            description: 'Employees have bank accounts for direct deposit',
            done: employees.filter(e => e.active !== false && e.isActive !== false).length > 0,
            hash: 'employee-setup',
            required: false,
        },
        {
            id: 'workers-comp',
            label: 'Workers Compensation',
            description: 'Workers comp carrier and policy number',
            done: !!(company?.workersCompCarrier || company?.workersCompPolicyNumber),
            hash: 'workers-compensation',
            required: false,
        },
        {
            id: 'prior-payroll',
            label: 'Prior Payroll (YTD)',
            description: 'Enter YTD balances if switching from another provider',
            done: ytdEntries.length > 0,
            hash: 'prior-payroll',
            required: false,
        },
        {
            id: 'filing-auth',
            label: 'Filing Authorization',
            description: 'At least one authorized signatory on file',
            done: signatories.length > 0,
            hash: 'filling-authorization',
            required: false,
        },
    ];

    const completed  = checks.filter(c => c.done).length;
    const total      = checks.length;
    const required   = checks.filter(c => c.required);
    const requiredDone = required.filter(c => c.done).length;
    const allRequiredDone = requiredDone === required.length;

    return { checks, completed, total, allRequiredDone };
}

// ── Dashboard card ────────────────────────────────────────────────────────────
export function SetupProgressCard() {
    const navigate = useNavigate();
    const { checks, completed, total, allRequiredDone } = useSetupChecks();
    const pct = Math.round((completed / total) * 100);

    if (allRequiredDone && pct === 100) return null; // fully complete — hide card

    return (
        <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 1px 8px rgba(0,0,0,0.08)',
            padding: '20px 24px',
            marginBottom: 24,
            border: '1px solid #e5e7eb',
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <h5 style={{ margin: 0, fontWeight: 700, color: '#1a3a5c', fontSize: 16 }}>
                        Company Setup
                    </h5>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#6b7280' }}>
                        {completed} of {total} steps complete
                        {!allRequiredDone && <span style={{ color: '#1B75F0', fontWeight: 600 }}> — required steps missing</span>}
                    </p>
                </div>
                <button
                    onClick={() => navigate('/dashboard/manage-company')}
                    style={{ background: '#1B75F0', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
                >
                    Complete Setup
                </button>
            </div>

            {/* Progress bar */}
            <div style={{ background: '#f3f4f6', borderRadius: 99, height: 8, marginBottom: 16, overflow: 'hidden' }}>
                <div style={{
                    height: '100%', borderRadius: 99,
                    width: `${pct}%`,
                    background: pct === 100 ? '#16a34a' : '#1B75F0',
                    transition: 'width 0.4s ease',
                }} />
            </div>

            {/* Checklist items */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                {checks.map(item => (
                    <div
                        key={item.id}
                        onClick={() => item.link
                            ? navigate(item.link)
                            : navigate(`/dashboard/manage-company${item.hash ? '#' + item.hash : ''}`)}
                        style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10,
                            padding: '8px 10px', borderRadius: 8, cursor: 'pointer',
                            background: item.done ? '#f0fdf4' : (item.required ? '#EFF6FF' : '#fafafa'),
                            border: `1px solid ${item.done ? '#bbf7d0' : (item.required ? '#fed7aa' : '#e5e7eb')}`,
                            transition: 'opacity 0.2s',
                        }}
                    >
                        <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>
                            {item.done ? '✅' : (item.required ? '⚠️' : '○')}
                        </span>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: item.done ? '#15803d' : '#374151' }}>
                                {item.label}
                                {!item.required && <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 11 }}> (optional)</span>}
                            </div>
                            <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>{item.description}</div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Sidebar indicator (small badge) ───────────────────────────────────────────
export function useSetupIndicators() {
    const { checks } = useSetupChecks();
    const byId = {};
    checks.forEach(c => { byId[c.id] = c.done; });
    return byId;
}

export default useSetupChecks;
